<script setup lang="ts">
import type { TurnstileValidationResponse } from '@nuxtjs/turnstile/runtime/types.js'

const { isLoading, start, finish } = useLoadingIndicator()
const { loggedIn, clear } = useUserSession()
const runtimeConfig = useRuntimeConfig()

const turnstileEnabled = runtimeConfig.public.TURNSTILE_ENABLED
const isLoadingTurnstileToken = ref(turnstileEnabled)
const turnstileToken = ref()

const setTurnstileToken = (value: string) => {
  isLoadingTurnstileToken.value = false
  turnstileToken.value = value
}

const reloadPage = () => {
  window.location.reload()
}

const handleLogin = async () => {
  start()

  try {
    if (!turnstileEnabled) {
      window.location.href = '/auth/auth0'
      return
    }

    const response = await $fetch<TurnstileValidationResponse>(
      '/turnstile/verify',
      {
        method: 'POST',
        body: {
          token: turnstileToken.value,
        },
      },
    )

    if (!response?.success) {
      throw new Error('Token validation failed')
    }

    window.location.href = '/auth/auth0'
  } catch (e) {
    const toast = useToast()

    toast.add({
      color: 'error',
      description: () =>
        h('span', { class: 'flex flex-col gap-2' }, [
          'Unable to validate cloudflare token. Please contact your administrator.',
          h(
            'button',
            {
              class: 'btn btn-text hover:border-secondary h-8 px-2 text-sm',
              onClick: reloadPage,
            },
            ['Refresh Page'],
          ),
        ]),
    })
  } finally {
    finish()
  }
}
</script>

<template>
  <div
    class="auth-bg auth-grid min-h-screen w-full sm:grid sm:content-center sm:gap-[160px] sm:p-20"
  >
    <AuthCard class="sm:w-[400px]">
      <div class="flex flex-1 flex-col justify-center">
        <link
          as="image"
          href="https://console-ui.development.rialtic.app/assets/landing_bg.svg"
          rel="preload"
        />
        <slot />

        <template v-if="!loggedIn">
          <NuxtTurnstile
            v-if="turnstileEnabled"
            :model-value="turnstileToken"
            @update:model-value="setTurnstileToken"
          />

          <UButton
            class="hover-shadow focus-shadow min-h-[52px] justify-center rounded-lg px-4 py-2 font-semibold text-white"
            :loading="isLoadingTurnstileToken || isLoading"
            color="secondary"
            external
            @click="handleLogin()"
          >
            Log in
          </UButton>
        </template>

        <div v-else class="flex flex-col space-y-4">
          <UButton
            to="/"
            class="justify-center rounded-lg px-4 py-2 font-semibold text-white"
            color="secondary"
            icon="i-ph-house"
          >
            Home
          </UButton>
          <UButton
            class="hover-shadow focus-shadow min-h-[52px] justify-center rounded-lg px-4 py-2 font-semibold text-white"
            color="secondary"
            @click="clear"
          >
            Log out
          </UButton>
        </div>
      </div>
    </AuthCard>
  </div>
</template>

<style scoped>
.auth-bg {
  background-color: #28b7f0;
  background-image: url('https://console-ui.development.rialtic.app/assets/landing_bg.svg');
  background-size: cover;
  background-position: center;
  background-image: url('data:image/svg+xml;base64,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');
}

.auth-grid {
  grid-template-rows: [left-start center-start right-start] 1fr [left-end center-end right-end];
  grid-template-columns: [left-start center-start] 1fr [left-end right-start] 1fr [center-end right-end];
  align-items: center;
  justify-content: center;
  justify-items: center;
  min-height: 100vh;
  height: fit-content;
}
.hover-shadow:hover {
  box-shadow: rgba(0, 0, 0, 0.1) 0px 0px 0px 150px inset;
}
.focus-shadow:focus {
  box-shadow:
    rgba(40, 183, 240, 0.15) 0px 0px 0px 4px,
    rgba(40, 183, 240, 0.15) 0px 0px 0px 150px inset;
}
</style>
